import Spinner from "@/components/spinner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useAppealReviewMutation } from "@/hooks/appeal-review.hook";
import { useCurrentUserProfile } from "@/hooks/current-user-profile.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { Send2 } from "iconsax-react";
import { useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";
import QuestionFieldset from "./question-fieldset";
import { questionSchema, reevaluationSchema } from "./reevaluation.schema";

type ReevaluationDrawerProps = {
  children: React.ReactNode;
};

type QuestionToSubmit = {
  questionId: string;
  questionNumber: string;
  comment: string;
};

const buildQuestionsToSubmit = (values: z.infer<typeof reevaluationSchema>) => {
  const questions: QuestionToSubmit[] = values.questions
    .filter((question) => question.questionId !== "")
    .map((question) => ({
      questionId: question.questionId,
      questionNumber: question.questionNumber,
      comment: question.comment,
    }));

  return questions;
};

const ReevaluationDrawer = (props: ReevaluationDrawerProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const [isAlertOpen, setIsAlertOpen] = useState(false);

  const [questionsToSubmit, setQuestionsToSubmit] = useState<
    Array<QuestionToSubmit>
  >([]);

  const { data: userProfile } = useCurrentUserProfile();

  const form = useForm<z.infer<typeof reevaluationSchema>>({
    resolver: zodResolver(reevaluationSchema),
    defaultValues: {
      questions: [
        { dimensionId: "", questionId: "", questionNumber: "", comment: "" },
        { dimensionId: "", questionId: "", questionNumber: "", comment: "" },
        { dimensionId: "", questionId: "", questionNumber: "", comment: "" },
      ],
    },
  });

  const { quizId } = useParams();

  const mutation = useAppealReviewMutation();

  const onSubmitForm = (values: z.infer<typeof reevaluationSchema>) => {
    setQuestionsToSubmit(buildQuestionsToSubmit(values));
    setIsAlertOpen(true);
  };

  const submitValues = () => {
    mutation.mutate(
      {
        companyId: userProfile?.companyId || "",
        applicationQuizId: quizId || "",
        details: questionsToSubmit,
      },
      {
        onSuccess: () => {
          toast("Se ha solicitado la re-evaluación");
          setIsAlertOpen(false);
          setQuestionsToSubmit([]);
          onOpenChange(false);
        },
      },
    );
  };

  const onOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      form.reset();
      setQuestionsToSubmit([]);
    }

    setIsOpen(isOpen);
  };

  const { fields } = useFieldArray({
    name: "questions",
    control: form.control,
  });

  const atLeastOneQuestionIsValid = form
    .getValues("questions")
    .some(
      (question) =>
        question.dimensionId !== "" &&
        questionSchema.safeParse(question).success,
    );

  return (
    <>
      <AlertDialog open={isAlertOpen} onOpenChange={setIsAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Vas a solicitar una re-evaluación
            </AlertDialogTitle>
            <AlertDialogDescription>
              Vas a apelar la evaluación de{" "}
              <strong>
                {questionsToSubmit.length}{" "}
                {questionsToSubmit.length > 1 ? "preguntas" : "pregunta"}
              </strong>
              . Esta acción es irreversible y solo puede realizarse una vez por
              edición.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={submitValues}>
              Solicitar re-evaluación
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Sheet open={isOpen} onOpenChange={onOpenChange}>
        <SheetTrigger asChild>{props.children}</SheetTrigger>

        <SheetContent className="flex flex-col">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmitForm)}
              className="contents"
            >
              <SheetHeader>
                <SheetTitle>Solicita re-evaluación</SheetTitle>
                <SheetDescription className="hidden">
                  Solicita la revisión de tus resultados
                </SheetDescription>
              </SheetHeader>

              <SheetBody className="grow overflow-auto">
                <div className="mb-4 flex flex-col gap-4">
                  <div className="flex flex-row gap-3 rounded-lg border border-[#615CB7] px-4 py-6">
                    <Send2 size={20} className="shrink-0 text-[#615CB7]" />

                    <div>
                      <h3 className="mb-2">Recuerda que...</h3>
                      <p className="text-sm">
                        Sólo podrás solicitar la re-evaluación de{" "}
                        <strong>3 preguntas</strong> en total y{" "}
                        <strong>solo 1 vez</strong>.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-6">
                  {fields.map((_, index) => (
                    <QuestionFieldset
                      key={index}
                      index={index}
                      quizId={quizId || ""}
                    />
                  ))}
                </div>
              </SheetBody>

              <SheetFooter className="grid grid-cols-2 items-center">
                <Button
                  type="submit"
                  variant="default"
                  size="lg"
                  disabled={mutation.isPending || !atLeastOneQuestionIsValid}
                >
                  {mutation.isPending && <Spinner className="mr-2" />}
                  Enviar solicitud
                </Button>

                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  onClick={() => onOpenChange(false)}
                >
                  Cancelar
                </Button>
              </SheetFooter>
            </form>
          </Form>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default ReevaluationDrawer;
